import moment from "moment";
import {
  CANCELLED_LIKE,
  FINAL_OK,
  IN_PROGRESS,
  PAIEMENT_FILTER,
  PAYMENT_TYPES,
  REFUND_TYPES,
  REJECTED_LIKE,
  STATUS_FILTER,
  TRANSACTION_STATUS,
  transactionDirection,
  TRX_METHOD_ENUM,
  TRX_STATUS_ENUM,
} from "../../enums/transaction";
import { PERIOD_FILTER } from "../../enums/period";
import { DocumentType } from "../../enums/document";
import { AllTransaction_allTransaction_content } from "../../graphql/graphql_transaction_linkcy/transaction/types/AllTransaction";
import {
  getIcon,
  getStatusStyle,
  getTransactionType,
} from "../Transaction/TransactionLogic";
import { arrondi } from "../../utils/common";
import { setPriceLocale } from "../../services/Locale";
import { TransactionPnvtInfo } from "../../graphql/graphql_transaction_linkcy/transaction/types/TransactionPnvtInfo";

export const getPaymentType = (type?: PAIEMENT_FILTER | null) => {
  switch (type) {
    case PAIEMENT_FILTER.INTER_LEDGER:
      return [PAYMENT_TYPES.MONEY_TRANSFER];
    case PAIEMENT_FILTER.SCT:
      return [PAYMENT_TYPES.SCT_RECEIVE, PAYMENT_TYPES.SCT_SEND];
    case PAIEMENT_FILTER.SCT_FAST:
      return [PAYMENT_TYPES.SCT_INST_SEND, PAYMENT_TYPES.SCT_INST_RECEIVE];
    case PAIEMENT_FILTER.SDD:
      return [PAYMENT_TYPES.SDD_SEND, PAYMENT_TYPES.SDD_RECEIVE];
    case PAIEMENT_FILTER.WITHDRAWAL:
      return [PAYMENT_TYPES.WITHDRAWAL];
    case PAIEMENT_FILTER.PAIEMENT:
      return [
        PAYMENT_TYPES.PURCHASE,
        PAYMENT_TYPES.UNIQUE_MCC_TRANSACTION,
        PAYMENT_TYPES.PURCHASE_RETURN_OR_REFUND,
      ];
    default:
      return [];
  }
};

export const getMethodsIds = (type?: PAIEMENT_FILTER | null) => {
  switch (type) {
    case PAIEMENT_FILTER.INTER_LEDGER:
      return [TRX_METHOD_ENUM.INTERNAL];
    case PAIEMENT_FILTER.SCT:
      return [TRX_METHOD_ENUM.SCT_SEPA];
    case PAIEMENT_FILTER.SCT_FAST:
      return [TRX_METHOD_ENUM.INST_SEPA];
    case PAIEMENT_FILTER.SDD:
      return [TRX_METHOD_ENUM.DDI_INTERNAL, TRX_METHOD_ENUM.SDD];
    case PAIEMENT_FILTER.CARD:
      return [TRX_METHOD_ENUM.CARD];
    default:
      return [];
  }
};

export const getPeriodDates = (
  period: PERIOD_FILTER,
): { startDate: string | null; endDate: string | null } => {
  const format = "YYYY-MM-DD[T]HH:mm";

  switch (period) {
    case "ALL":
      // Pour ALL, aucune date n'est définie
      return { startDate: null, endDate: null };

    case "DAY":
      // Pour la journée, on définit le début à 00:00 et la fin à 23:59
      return {
        startDate: moment().startOf("day").format(format),
        endDate: moment().endOf("day").format(format),
      };

    case "WEEK":
      // Pour la semaine : du lundi 00:00 au dimanche 23:59
      return {
        startDate: moment().startOf("isoWeek").startOf("day").format(format),
        endDate: moment().endOf("isoWeek").endOf("day").format(format),
      };

    case "MONTH":
      // Pour le mois courant : du premier jour à 00:00 au dernier jour à 23:59
      return {
        startDate: moment().startOf("month").startOf("day").format(format),
        endDate: moment().endOf("month").endOf("day").format(format),
      };

    default:
      return { startDate: null, endDate: null };
  }
};

export const getStatusTransaction = (type?: STATUS_FILTER | null) => {
  switch (type) {
    case STATUS_FILTER.VALIDATED:
      return [
        TRX_STATUS_ENUM.COMPLETED,
        TRX_STATUS_ENUM.ACCEPTED,
        TRX_STATUS_ENUM.AML_APPROVED,
        TRX_STATUS_ENUM.CREDIT_TECH_BALANCE,
      ];
    case STATUS_FILTER.PENDING:
      return [
        TRX_STATUS_ENUM.CREATED,
        TRX_STATUS_ENUM.SIGNED,
        TRX_STATUS_ENUM.REVIEW,
        TRX_STATUS_ENUM.READY_TO_CLEAR,
        TRX_STATUS_ENUM.SENT_TO_PACK,
        TRX_STATUS_ENUM.PACKED,
        TRX_STATUS_ENUM.SENT_TO_CLEAR,
        TRX_STATUS_ENUM.TO_SIGN,
        TRX_STATUS_ENUM.RESERVED,
        TRX_STATUS_ENUM.TO_RESERVE,
        TRX_STATUS_ENUM.RECEIVED,
        TRX_STATUS_ENUM.ON_HOLD,
        TRX_STATUS_ENUM.READY_FOR_SCREENING,
        TRX_STATUS_ENUM.AML_SCANNING,
        TRX_STATUS_ENUM.BLACKLIST_SCANNING,
        TRX_STATUS_ENUM.MANUAL_ACTION,
        TRX_STATUS_ENUM.TO_RESEND,
      ];
    case STATUS_FILTER.DECLINED:
      return [
        TRX_STATUS_ENUM.REJECTED,
        TRX_STATUS_ENUM.MANUAL_REJECTED,
        TRX_STATUS_ENUM.AML_REJECTED,
        TRX_STATUS_ENUM.INVALID,
      ];
    case STATUS_FILTER.REVERSED:
      return [
        TRX_STATUS_ENUM.CANCELLED,
        TRX_STATUS_ENUM.DROPPED,
        TRX_STATUS_ENUM.TO_CANCEL,
      ];
    default:
      return [];
  }
};

export const getTransactionDirection = (
  direction?: transactionDirection | null,
) => {
  switch (direction) {
    case transactionDirection.CREDIT:
      return "IN";
    case transactionDirection.DEBIT:
      return "OUT";
    default:
      return null;
  }
};

export const DocumentsTransaction = [
  DocumentType.OTHER,
  DocumentType.INVOICE,
  DocumentType.PAYSLIP,
  DocumentType.SOURCE_OF_FUNDS,
  DocumentType.BANK_ACCOUNT_STATEMENT,
  DocumentType.BANK_ACCOUNT_DETAILS,
];

export interface FileTransaction {
  isPresent: boolean;
  color: string;
  type: string;
  typeName: string;
  linkcyId: string;
  status: string;
}

export const getInfoTransaction = (
  transaction: TransactionPnvtInfo,
  accountUsed?: number | null,
) => {
  const {
    id,
    amount,
    creationDate,
    creditor,
    debtor,
    typeId,
    statusId,
    methodeId,
    billingType,
  } = transaction;
  const icon = methodeId ? getIcon(methodeId) : "💸";
  const type: "credit" | "debit" =
    accountUsed == creditor?.accountPnvtId ? "credit" : "debit";
  const modalityText = methodeId
    ? getTransactionType(methodeId, typeId, billingType)
    : "-";
  const beneficary = accountUsed == creditor?.accountPnvtId ? debtor : creditor;
  const _amount = setPriceLocale(arrondi(amount!));
  const { label, color, icon: _icon } = getStatusStyle(statusId, typeId);
  const name =
    beneficary?.name || beneficary?.bankName || beneficary?.iban || "-";
  return {
    ...transaction,
    creationDate,
    id,
    icon,
    type,
    modalityText,
    statusContent: { label, color, icon: _icon },
    name,
    amount: _amount,
    beneficary,
  };
};
